import type { Schema } from '@dealdesk/api-schema';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import { FINANCE_QUERY, STALE } from '@/shared/api/stale';

import { staffCommissionKeys, type StaffListParams, type StaffScope } from './keys';

const scope = (s: StaffScope) => ({ period: s.period, ...(s.staffType ? { staffType: s.staffType } : {}) });
const listQuery = (p: StaffListParams) => ({
  ...scope(p),
  ...(p.q ? { q: p.q } : {}),
  ...(p.sort ? { sort: p.sort } : {}),
  ...(p.page ? { page: p.page } : {}),
  ...(p.size ? { size: p.size } : {}),
});

/** Staff commission is finance data: never cached as fresh (FINANCE_QUERY). */
export const overviewQuery = (s: StaffScope) =>
  queryOptions({
    queryKey: staffCommissionKeys.overview(s),
    queryFn: ({ signal }) => unwrap(api.GET('/staff-commission/overview', { params: { query: scope(s) }, signal })),
    ...FINANCE_QUERY,
  });

/** Chart series: the overview totals of the last six months for the people the caller may see. */
export const trendQuery = (s: StaffScope) =>
  queryOptions({
    queryKey: staffCommissionKeys.trend(s),
    queryFn: ({ signal }) => unwrap(api.GET('/staff-commission/trend', { params: { query: { ...scope(s), periods: 6 } }, signal })),
    ...FINANCE_QUERY,
  });

/** My commission statement history: the caller's monthly totals for the twelve months ending at `period` (own scope). */
export const historyQuery = (period: string) =>
  queryOptions({
    queryKey: staffCommissionKeys.history(period),
    queryFn: ({ signal }) => unwrap(api.GET('/staff-commission/trend', { params: { query: { period, periods: 12 } }, signal })),
    ...FINANCE_QUERY,
  });

export const statementsQuery = (p: StaffListParams) =>
  queryOptions({
    queryKey: staffCommissionKeys.statements(p),
    queryFn: ({ signal }) => unwrap(api.GET('/staff-commission/statements', { params: { query: listQuery(p) }, signal })),
    ...FINANCE_QUERY,
    placeholderData: keepPreviousData,
  });

export const statementQuery = (userId: string, period: string) =>
  queryOptions({
    queryKey: staffCommissionKeys.statement(userId, period),
    queryFn: ({ signal }) => unwrap(api.GET('/staff-commission/statements/{userId}/{period}', { params: { path: { userId, period } }, signal })),
    ...FINANCE_QUERY,
  });

export const rulesQuery = (q?: string) =>
  queryOptions({
    queryKey: staffCommissionKeys.rules(q),
    queryFn: ({ signal }) => unwrap(api.GET('/staff-commission/rules', { params: { query: q ? { q } : {} }, signal })),
    ...FINANCE_QUERY,
    placeholderData: keepPreviousData,
  });

export const runsQuery = () =>
  queryOptions({
    queryKey: staffCommissionKeys.runs(),
    queryFn: ({ signal }) => unwrap(api.GET('/staff-commission/payout-runs', { signal })),
    ...FINANCE_QUERY,
  });

export const runQuery = (runId: string) =>
  queryOptions({
    queryKey: staffCommissionKeys.run(runId),
    queryFn: ({ signal }) => unwrap(api.GET('/staff-commission/payout-runs/{runId}', { params: { path: { runId } }, signal })),
    ...FINANCE_QUERY,
  });

export const adjustmentsQuery = (p: StaffListParams) =>
  queryOptions({
    queryKey: staffCommissionKeys.adjustments(p),
    queryFn: ({ signal }) => unwrap(api.GET('/staff-commission/adjustments', { params: { query: listQuery(p) }, signal })),
    ...FINANCE_QUERY,
    placeholderData: keepPreviousData,
  });

export const clawbacksQuery = (p: StaffListParams) =>
  queryOptions({
    queryKey: staffCommissionKeys.clawbacks(p),
    queryFn: ({ signal }) => unwrap(api.GET('/staff-commission/clawbacks', { params: { query: listQuery(p) }, signal })),
    ...FINANCE_QUERY,
    placeholderData: keepPreviousData,
  });

export const dealStaffCommissionQuery = (dealId: string) =>
  queryOptions({
    queryKey: staffCommissionKeys.deal(dealId),
    queryFn: ({ signal }) => unwrap(api.GET('/deals/{dealId}/staff-commission', { params: { path: { dealId } }, signal })),
    ...FINANCE_QUERY,
  });

export const schemesQuery = (bankId: string, product: Schema<'ProductCode'>) =>
  queryOptions({
    queryKey: staffCommissionKeys.schemes(bankId, product),
    queryFn: ({ signal }) => unwrap(api.GET('/banks/{bankId}/products/{productCode}/schemes', { params: { path: { bankId, productCode: product } }, signal })),
    staleTime: STALE.reference,
  });
